/**
 * Fixed-capacity FIFO ring buffer.
 *
 * Implemented with a circular index so push() is O(1) — Array.prototype.shift()
 * is deliberately avoided to prevent O(n) cost on every write.
 */
export class RingBuffer<T> {
  private readonly capacity: number;
  private readonly buf: (T | undefined)[];
  private head = 0; // index of the oldest valid slot (read pointer)
  private tail = 0; // index of the next write slot
  private count = 0;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.buf = new Array(capacity);
  }

  push(item: T): void {
    this.buf[this.tail] = item;
    this.tail = (this.tail + 1) % this.capacity;

    if (this.count < this.capacity) {
      this.count++;
    } else {
      // Buffer is full — advance head to overwrite the oldest slot
      this.head = (this.head + 1) % this.capacity;
    }
  }

  /**
   * Returns a snapshot in chronological order (oldest first).
   * Mutations to the returned array do not affect internal state.
   */
  snapshot(): T[] {
    const result: T[] = new Array(this.count);
    for (let i = 0; i < this.count; i++) {
      result[i] = this.buf[(this.head + i) % this.capacity] as T;
    }
    return result;
  }

  clear(): void {
    this.head = 0;
    this.tail = 0;
    this.count = 0;
    this.buf.fill(undefined);
  }

  size(): number {
    return this.count;
  }
}
